// De talen waarin een vragenlijst aangeboden kan worden.
//
// Sranantongo staat er bewust niet in: daarin wordt niet vertaald (Google kent
// het niet, het taalmodel vertaalde onbetrouwbaar), dus die keuze zou gewoon
// Nederlands tonen. Een oude link met /srn blijft werken en toont Nederlands.
// ADR-055.
//
// `readAloud` zegt of onze eigen stemdienst (tts-service/) deze taal kan
// voorlezen. Vertalen kan in álle talen hieronder; voorlezen niet — voor zes
// talen bestaat er geen Piper-stem. Staat de vlag op false, dan verdwijnt de
// voorleesknop, want een knop die stil niets doet is erger dan geen knop.
//
// De vlag is GEEN losse lijst: `tts-service/voices.json` is de bron, en
// `listOfLanguages.test.js` maakt deze twee rood zodra ze uit elkaar lopen.
// AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment

/**
 * @typedef {object} Language
 * @property {string} code - ISO-taalcode zoals die in de vragenlijst-url staat
 * @property {string} name - Nederlandse naam van de taal
 * @property {string} flag - Vlagemoji, alleen ter herkenning in de keuzelijst
 * @property {boolean} readAloud - Of de stemdienst deze taal kan voorlezen
 */

/** @type {Language[]} */
const languages = [
    { code: "ar", name: "Arabisch", flag: "🇸🇦", readAloud: true },
    { code: "am", name: "Amhaars", flag: "🇪🇹", readAloud: false },
    { code: "bn", name: "Bengaals", flag: "🇧🇩", readAloud: true },
    { code: "zh", name: "Chinees", flag: "🇨🇳", readAloud: true },
    { code: "de", name: "Duits", flag: "🇩🇪", readAloud: true },
    { code: "en", name: "Engels", flag: "🇺🇸", readAloud: true },
    { code: "fa", name: "Perzisch", flag: "🇮🇷", readAloud: true },
    { code: "fy", name: "Fries", flag: "🇳🇱", readAloud: false },
    { code: "fr", name: "Frans", flag: "🇫🇷", readAloud: true },
    { code: "hi", name: "Hindi", flag: "🇮🇳", readAloud: true },
    { code: "it", name: "Italiaans", flag: "🇮🇹", readAloud: true },
    { code: "ja", name: "Japans", flag: "🇯🇵", readAloud: true },
    { code: "ko", name: "Koreaans", flag: "🇰🇷", readAloud: true },
    { code: "nl", name: "Nederlands", flag: "🇳🇱", readAloud: true },
    { code: "pap", name: "Papiaments", flag: "🇨🇼", readAloud: false },
    { code: "pl", name: "Pools", flag: "🇵🇱", readAloud: true },
    { code: "pt", name: "Portugees", flag: "🇵🇹", readAloud: true },
    { code: "ru", name: "Russisch", flag: "🇷🇺", readAloud: true },
    { code: "es", name: "Spaans", flag: "🇪🇸", readAloud: true },
    { code: "so", name: "Somalisch", flag: "🇸🇴", readAloud: false },
    { code: "sv", name: "Zweeds", flag: "🇸🇪", readAloud: true },
    { code: "th", name: "Thai", flag: "🇹🇭", readAloud: true },
    { code: "ti", name: "Tigrinya", flag: "🇪🇷", readAloud: false },
    { code: "tr", name: "Turks", flag: "🇹🇷", readAloud: true },
    { code: "vi", name: "Vietnamees", flag: "🇻🇳", readAloud: true }
];

/**
 * De taalcodes die voorgelezen kunnen worden.
 *
 * @returns {string[]}
 */
const readAloudLanguageCodes = () =>
    languages.filter(language => language.readAloud).map(language => language.code);

/**
 * Kan deze taal voorgelezen worden?
 *
 * De url draagt soms een regio mee ('en-GB', 'nl_BE'); de stemdienst kijkt
 * alleen naar de eerste twee letters, dus hier ook. Geen taalcode betekent
 * Nederlands — de brontaal van elke vragenlijst.
 *
 * @param {string} [code] - Taalcode uit de url
 * @returns {boolean}
 */
const hasReadAloudVoice = (code) => {
    const base = String(code || 'nl').toLowerCase().slice(0, 2);
    return languages.some(language => language.code === base && language.readAloud);
};

export { readAloudLanguageCodes, hasReadAloudVoice };
export default languages;
